import { quotationAreaKey, withAreaNickname } from './quotationAreas';
import InputControl from '@components/InputControl';
import RemarkHistory from '@components/RemarkHistory';
import { CustomerActionType } from '@store/branches/customer/enums';
import { LeadsActionType } from '@store/branches/leads/enums';
import { ceilQuotationAmount, formatCurrencyINR, toast, mergeQuotationProductForUpdate } from '@utilities';
import { Button } from 'primereact/button';
import { confirmPopup } from 'primereact/confirmpopup';
import { InputText } from 'primereact/inputtext';
import React, { useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import AddRemarkModal from '@components/AddRemarkModal';
import { Sidebar } from 'primereact/sidebar';
import TwoDView from '@components/TwoDView';
import { Image } from 'primereact/image';
import { Edit2, Gallery, Message } from 'iconsax-react';

export const sqftToMm = (sqft: number) => sqft * 92903.04;
export const mmToSqft = (mm: number) => mm / 92903.04;

export const fetchBuilderFCData = ({ dispatch, currentQuotationItem, successCB, errorCB }: Record<string, any>) => {
	dispatch({
		type: LeadsActionType.FETCH_BUILDER_FC_DATA,
		payload: {
			productId: currentQuotationItem?.productId,
			successCB,
			errorCB: (message: string) => {
				if (message !== "doesn't exist!") toast('ERROR', message);
				errorCB?.(message);
			}
		}
	});
};

export const changeAreaNickname = (
	dispatch: any,
	areaName: string,
	nickname: string,
	quotationID: string,
	setDisplayedQuotationList: any,
	setEditingAreaName: any,
	setSelectedNickname: any,
	floor?: number
) => {
	dispatch({
		type: LeadsActionType.CHANGE_AREA_NICKNAME,
		payload: {
			quotationID,
			areaName,
			nickname,
			floor: Number(floor ?? 0),
			successCB: () => {
				toast('SUCCESS', 'Nickname updated successfully');
				setDisplayedQuotationList(prevList =>
					withAreaNickname(prevList, { areaName, floor }, nickname)
				);
				setEditingAreaName(null);
				setSelectedNickname('');
			},
			errorCB: message => {
				toast('ERROR', message);
			}
		}
	});
};

export const changeQuotationImage = (
	dispatch,
	editingProduct,
	docId,
	quotationID,
	setImageModalOpen,
	fetchQuotationsList
) => {
	dispatch({
		type: LeadsActionType.CHANGE_QUOTATION_IMAGE,
		payload: {
			formData: {
				quotationID,
				areaName: editingProduct.areaName,
				areaEnum: editingProduct.areaEnum,
				floor: editingProduct.floor,
				productId: editingProduct.productId,
				documentId: docId
			},
			successCB: () => {
				toast('SUCCESS', 'Image uploaded');
				setImageModalOpen(false);
				fetchQuotationsList(true);
			},
			errorCB: (msg: string) => toast('ERROR', msg)
		}
	});
};

export const copyQuotationItem = (dispatch, item, quotationID, setIsCopyDialogOpen, fetchQuotationsList) => {
	dispatch({
		type: LeadsActionType.COPY_QUOTATION_ITEM,
		payload: {
			formData: {
				...item,
				areaName: typeof item.areaName === 'string' ? item.areaName : item.areaName?.areaName,
				productId: item.productId,
				builder: item.builder,
				customProduct: Boolean(item.customProduct)
			},
			quotationID,
			successCB: () => {
				toast('SUCCESS', item.customProduct ? 'Submitted for approval' : 'Quotation Item Copied');
				setIsCopyDialogOpen(false);
				fetchQuotationsList(true);
			},
			errorCB: (message: string) => {
				toast('ERROR', message);
				setIsCopyDialogOpen(false);
			}
		}
	});
};

export const handleDeleteItem = (event, itemId, setDisplayedQuotationList, updateQuotationItem) => {
	confirmPopup({
		target: event.currentTarget,
		message: 'Are you sure you want to delete this product?',
		icon: 'pi pi-exclamation-triangle',
		defaultFocus: 'accept',
		accept: () => {
			setDisplayedQuotationList(prevList =>
				prevList
					.map(area => ({
						...area,
						products: area.products?.filter(product => product.productId !== itemId)
					}))
					.filter(area => area.products?.length)
			);
			updateQuotationItem(itemId, 'delete');
		},
		reject: () => {}
	});
};

export const fetchChat = (dispatch, areaChat) => {
	dispatch({
		type: CustomerActionType.FETCH_TICKET_CHAT,
		payload: {
			ticketId: areaChat,
			errorCB: err => {
				toast('ERROR', err);
			}
		}
	});
};

export const handleSubmitQuotationRowData = (
	updatedVal: any,
	currentItem,
	updateQuotationItem,
	setEditModalVisible
) => {
	if (!currentItem) return;
	const price = ceilQuotationAmount(updatedVal?.price ?? updatedVal?.total);
	const enriched = mergeQuotationProductForUpdate(currentItem, {
		...updatedVal,
		priceIsBase: updatedVal?.priceIsBase ?? true,
		price,
		total: ceilQuotationAmount(updatedVal?.total ?? price),
		allowProductNameUpdate: true,
		productName: updatedVal?.productName ?? currentItem?.productName,
		productId: currentItem.productId || updatedVal.productId,
		productCategoryName: currentItem.productCategoryName,
		productSubCategoryName: currentItem.productSubCategoryName,
		areaEnum: currentItem.areaEnum,
		areaName: currentItem.areaName,
		floor: currentItem.floor,
		type: currentItem.type,
		builder: currentItem.builder,
		active: currentItem.active !== undefined ? currentItem.active : true
	});
	updateQuotationItem(enriched, 'update');
	setEditModalVisible && setEditModalVisible(false);
};

export const renderSpecificationOrArea = rowData => {
	if (rowData.unitOfMeasure !== 'SQFT') return 'N/A';
	const isArea = rowData.calculation?.includes('Area');
	const isDimensions = /(w|h|d)\s*\*\s*r/i.test(rowData.calculation);

	const formatValue = value => (value > 0 ? value : '-');

	if (isArea && rowData?.measurement?.area !== undefined) {
		const sqft = mmToSqft(rowData.measurement.area);
		return `Area: ${sqft.toFixed(2)}`;
	}

	if (isDimensions) {
		const width = formatValue(rowData.measurement?.width);
		const height = formatValue(rowData.measurement?.height);
		const depth = formatValue(rowData.measurement?.depth);

		return `W: ${width} X H: ${height} X D: ${depth}`;
	}

	return 'N/A';
};

export const rowGroupHeaderTemplate = (
	data,
	updatedQuotationList,
	editingAreaName,
	selectedNickname,
	setSelectedNickname,
	onAccept,
	onReject,
	onClickAddNickName,
	areaSubTotal, // <-- new optional prop
	dispatch,
	chatHistory,
	overlayPanelRef,
	predictionId
) => {
	const area = updatedQuotationList?.find(
		area => quotationAreaKey(area) === quotationAreaKey(data)
	);
	const isEditing = editingAreaName === quotationAreaKey(data);
	const sortedHistory = [...(chatHistory || [])].sort((a, b) => a.timestamp - b.timestamp);

	const getTwoDJSON = () => {
		dispatch({
			type: CustomerActionType.FETCH_2D_JSON,
			payload: {
				predictionId: predictionId,
				currentFloor: area?.floor || 0,
				errorCB: (message: string) => {
					toast('ERROR', message);
				}
			}
		});
	};

	const [view2DSidebar, setView2DSidebar] = useState(false);
	const [selected2DArea, setSelected2DArea] = useState('');
	const [selected2DProductName, setSelected2DProductName] = useState<string>('');
	const [selectedProducts, setSelectedProducts] = useState<Array<Record<string, any>>>([]);

	const renderProductList = () => (
		<div className="overflow-y-auto show-scrollbar" style={{ maxHeight: '80vh' }}>
			{selectedProducts.map(product => {
				const { productId, productName, imageUrl, measurement, calculation } = product;
				const { width, height, depth, area } = measurement || {};

				let dimensionDisplay = '';
				if (calculation === 'W*H*R') {
					dimensionDisplay = `W: ${width} x H: ${height}`;
				} else if (calculation === 'W*D*R') {
					dimensionDisplay = `W: ${width} x D: ${depth || '—'}`;
				} else if (calculation === 'A*R') {
					dimensionDisplay = `A: ${area}`;
				}

				return (
					<div
						key={productId}
						className={`py-6 border-b last:border-b-0 cursor-pointer ${
							productName === selected2DProductName ? 'bg-[var(--color-surface)]' : ''
						}`}
						onClick={() => setSelected2DProductName(productName)}
					>
						<div className="flex items-center gap-3">
							<div className="flex-shrink-0 w-10 h-10 overflow-hidden bg-[var(--color-surface)] rounded">
								<Image
									src={imageUrl}
									alt={productName}
									className="!w-full !h-full !object-cover"
									loading="lazy"
								/>
							</div>
							<div className="flex flex-col">
								<span className="text-sm text-[var(--color-text-primary)]">{productName}</span>
								{dimensionDisplay && <span className="text-xs text-[var(--color-text-tertiary)]">{dimensionDisplay}</span>}
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);

	return (
		<div className="flex items-center justify-between w-full px-3 bg-transparent" style={{ minHeight: '44px' }}>
			<div className="flex items-center gap-2 min-w-0">
				<div className="flex flex-col min-w-0">
					<div className="flex items-center gap-2 flex-wrap min-w-0">
						<span className="text-sm font-semibold text-skin-base truncate">{data.areaName}</span>
						{isEditing ? (
							<>
								<InputText
									value={selectedNickname}
									onChange={e => {
										e.preventDefault();
										e.stopPropagation();
										setSelectedNickname(e.target.value);
									}}
									className="w-32 ml-1"
									style={{ height: '28px', fontSize: '12px' }}
								/>
								<Button icon="pi pi-check" className="ml-1 !py-1 !px-2 !text-xs" type="button" onClick={onAccept} />
								<Button icon="pi pi-times" className="ml-1 !py-1 !px-2 !text-xs" type="button" onClick={onReject} />
							</>
						) : (
							<>
								{data.nickname && (
									<span className="text-[11px] text-skin-faint truncate">— {data.nickname}</span>
								)}
								<button
									type="button"
									title="Rename room"
									className="ml-1 w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 text-skin-faint hover:text-skin-base hover:bg-skin-hover transition-colors bg-transparent border-none cursor-pointer"
									onClick={e => {
										e.stopPropagation();
										onClickAddNickName();
									}}
								>
									<Edit2 size={13} color="currentColor" variant="Linear" />
								</button>
							</>
						)}
					</div>
				</div>
			</div>
			<div className="flex items-center gap-1.5 flex-shrink-0">
				<button
					type="button"
					className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-skin-card border border-skin-strong shadow-soft text-[11px] font-semibold text-skin-base hover:bg-skin-hover transition-colors cursor-pointer"
					onClick={e => {
						e.stopPropagation();
						setView2DSidebar(true);
						getTwoDJSON();
						setSelected2DArea(data.areaName.toLowerCase().replace(/\s+/g, ''));
						setSelectedProducts(data.products);
						setSelected2DProductName(() => data?.products?.[0]?.productName || '');
					}}
				>
					<Gallery size={13} color="currentColor" variant="Linear" />
					View 2D
				</button>
				<button
					type="button"
					className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-skin-card border border-skin-strong shadow-soft text-[11px] font-semibold text-skin-base hover:bg-skin-hover transition-colors cursor-pointer"
					onClick={e => {
						e.stopPropagation();
						overlayPanelRef.current?.toggle(e);
						fetchChat(dispatch, area.areaChat);
					}}
				>
					<Message size={13} color="currentColor" variant="Linear" />
					Remark
				</button>
				<OverlayPanel
					ref={overlayPanelRef}
					pt={{
						root: { className: 'w-[400px] rounded-lg right-0 absolute' },
						content: { className: 'p-3' }
					}}
				>
					<RemarkHistory messages={sortedHistory} />
					{sortedHistory.length !== 0 && (
						<>
							<AddRemarkModal
								areaName={area.areaName}
								onCancel={() => {
									overlayPanelRef.current?.hide();
								}}
								ticketId={area.areaChat}
								showHistory={false}
							/>
						</>
					)}
				</OverlayPanel>

				<Sidebar
					visible={view2DSidebar}
					position="right"
					onHide={() => {
						setView2DSidebar(false);
					}}
					modal={false}
					style={{ height: '90%', width: '100%' }}
				>
					<div className="grid grid-cols-[20%,80%] gap-4 p-4 h-full">
						<div className="flex flex-col h-full overflow-hidden border border-[var(--color-border)] rounded-lg">
							{/* Fixed Header */}
							<div className="flex-shrink-0 p-4 bg-[var(--color-card)] border-b border-[var(--color-border)]">
								<h1 className="text-xl font-bold text-[var(--color-text-secondary)]">Products</h1>
							</div>
							<div className="flex-1 overflow-y-auto show-scrollbar">{renderProductList()}</div>
						</div>

						<div className="flex flex-col items-center h-full gap-2">
							{/* View Content */}
							<div className="flex-1 w-full">
								<TwoDView
									selectedCategory="liteQuotation"
									selectedProduct={selected2DProductName}
									selected2DArea={selected2DArea}
								/>
							</div>
						</div>
					</div>
				</Sidebar>

				<span className="text-sm font-bold tabular-nums text-skin-base ml-1.5 min-w-[90px] text-right">{formatCurrencyINR(
					areaSubTotal !== undefined ? areaSubTotal : area?.activeSubTotal || 0,
					0,
					'ceil'
				)}</span>
			</div>
		</div>
	);
};

export const renderDimensionInputs = (currentItem, editForm, editable) => {
	if (!currentItem) return null;

	// For custom products, always allow editing of all dimensions regardless of builder/calculation
	if (currentItem.customProduct) {
		return (
			<div className="flex gap-2">
				<InputControl
					type="number"
					tag="measurement.width"
					label="Width (mm)"
					formObj={editForm}
				/>

				<InputControl
					type="number"
					tag="measurement.height"
					label="Height (mm)"
					formObj={editForm}
				/>
				<InputControl
					type="number"
					tag="measurement.depth"
					label="Depth (mm)"
					formObj={editForm}
				/>
			</div>
		);
	}
	// FC (False Ceiling): Area = Width × Depth. Show the dimensions that feed the Area as
	// read-only — FC values are managed via the builder, not edited here.
	if (currentItem.builder === 'FC') {
		return (
			<div className="flex gap-2">
				<InputControl type="number" tag="measurement.width" label="Width (mm)" formObj={editForm} disabled />
				<InputControl type="number" tag="measurement.depth" label="Depth (mm)" formObj={editForm} disabled />
			</div>
		);
	}
	if (
		currentItem.unitOfMeasure === 'SQFT' &&
		!currentItem.builder &&
		/(W\*H\*R|W\*D\*R)/.test(currentItem.calculation)
	) {
		return (
			<div className="flex gap-2">
				<InputControl
					type="number"
					tag="measurement.width"
					label="Width (mm)"
					formObj={editForm}
					disabled={!editable.includes('width') && !currentItem.builder}
				/>

				<InputControl
					type="number"
					tag="measurement.height"
					label="Height (mm)"
					formObj={editForm}
					disabled={!editable.includes('height')}
				/>
				<InputControl
					type="number"
					tag="measurement.depth"
					label="Depth (mm)"
					formObj={editForm}
					disabled={!editable.includes('depth')}
				/>
			</div>
		);
	}
	if (currentItem.unitOfMeasure === 'UNIT' && !currentItem.builder) {
		return (
			<div className="flex gap-2">
				<InputControl type="number" tag="measurement.width" label="Width (mm)" formObj={editForm} />
				<InputControl type="number" tag="measurement.height" label="Height (mm)" formObj={editForm} />
				<InputControl type="number" tag="measurement.depth" label="Depth (mm)" formObj={editForm} />
			</div>
		);
	}
	return null;
};

export const renderAreaInput = (currentItem, editForm, editable) => {
	if (!currentItem) return null;

	// For custom products, always allow editing of area when it exists
	if (currentItem.customProduct) {
		return (
			<InputControl
				type="number"
				tag="measurement.area"
				label="Area (sqft)"
				formObj={editForm}
				mode="decimal"
				minFractionDigits={0}
				maxFractionDigits={2}
			/>
		);
	}
	// FC builder: Area comes from the API and is shown read-only (managed via the builder).
	if (currentItem.builder === 'FC') {
		return (
			<InputControl
				type="number"
				tag="measurement.area"
				label="Area (sqft)"
				formObj={editForm}
				mode="decimal"
				minFractionDigits={0}
				maxFractionDigits={2}
				disabled
			/>
		);
	}
	if (currentItem.unitOfMeasure === 'SQFT' && !currentItem.builder) {
		if (/Area/.test(currentItem.calculation)) {
			return (
				<InputControl
					type="number"
					tag="measurement.area"
					label="Area (sqft)"
					formObj={editForm}
					mode="decimal"
					minFractionDigits={0}
					maxFractionDigits={2}
					disabled={!editable.includes('area') || currentItem.builder}
				/>
			);
		}
		if (/W\*(H|D)\*R/.test(currentItem.calculation)) {
			return (
				<InputControl type="number" tag="measurement.area" label="Area (sqft)" formObj={editForm} disabled />
			);
		}
	}
	return null;
};

export const handleSubmitQuotationRowDataForFC = (
	updatedVal,
	currentItem,
	updateQuotationItem,
	setEditModalVisible
) => {
	const price = ceilQuotationAmount(updatedVal?.price ?? updatedVal?.total ?? currentItem?.price);
	const enriched = mergeQuotationProductForUpdate(currentItem, {
		...updatedVal,
		priceIsBase: updatedVal?.priceIsBase ?? true,
		price,
		total: ceilQuotationAmount(updatedVal?.total ?? price),
		active: true,
		productId: currentItem.productId,
		productName: currentItem.productName,
		imageUrl: currentItem.imageUrl,
		measurement: updatedVal.measurement || {
			width: currentItem.measurement?.width,
			height: currentItem.measurement?.height,
			depth: currentItem.measurement?.depth,
			area: currentItem.measurement?.area
		},
		quantity: currentItem.quantity,
		unitCost: currentItem.unitCost,
		calculation: currentItem.calculation,
		unitOfMeasure: currentItem.unitOfMeasure,
		productCategoryName: currentItem.productCategoryName,
		productSubCategoryName: currentItem.productSubCategoryName,
		areaEnum: currentItem.areaEnum,
		areaName: currentItem.areaName,
		floor: currentItem.floor,
		type: currentItem.type,
		builder: currentItem.builder
	});
	updateQuotationItem(enriched, 'update', 'fcBuilder');
	if (setEditModalVisible) {
		setEditModalVisible(false);
	}
};
